import { useState, useEffect, useRef } from "react";
import axios from "axios";
import Logo from '../components/Logo';

const STEPS = {
  FORM: "form",
  PROCESSING: "processing",
  SUCCESS: "success",
  ERROR: "error",
  PAID: 'paid',
};

// 0.02 → "0.02 m³ (20 litres)"
const formatUnits = (u) => {
  const n = Number(u);
  if (!Number.isFinite(n) || n <= 0) return null;
  const m3 = `${n.toLocaleString('en-KE', { maximumFractionDigits: 3 })} m³`;
  return n < 10 ? `${m3} (${Math.round(n * 1000).toLocaleString('en-KE')} litres)` : m3;
};

// ─────────────────────────────────────────
// TokenDisplay Component
// Displays the real token from the database
// ─────────────────────────────────────────
function TokenDisplay({ token, units }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div style={{ background: "rgba(56,189,248,0.08)", border: "1.5px dashed #38bdf8", borderRadius: 12, padding: "16px", textAlign: "center", marginTop: 8 }}>
      <div style={{ color: "#94a3b8", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>Token Number</div>
      <div style={{ fontFamily: "monospace", fontSize: "clamp(20px, 6vw, 28px)", fontWeight: 800, letterSpacing: "clamp(3px, 1.5vw, 6px)", color: "#38bdf8", marginBottom: 10, wordBreak: "break-all" }}>{token}</div>
      <button onClick={copy} style={{ background: copied ? "#10b981" : "rgba(56,189,248,0.15)", color: copied ? "#fff" : "#38bdf8", border: "none", borderRadius: 8, padding: "7px 20px", cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s" }}>
        {copied ? "✓ Copied!" : "Copy Token"}
      </button>
      {formatUnits(units) && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(56,189,248,0.2)" }}>
          <div style={{ color: "#94a3b8", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>Units Purchased</div>
          <div style={{ color: "#e2e8f0", fontSize: 20, fontWeight: 800 }}>{formatUnits(units)}</div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
// PaymentPage Component
// Main payment form — talks to real backend
// ─────────────────────────────────────────
export default function PaymentPage() {

  // ── STATE ──────────────────────────────
  // Controls which screen is shown
  const [step, setStep] = useState(STEPS.FORM);

  // Form field values
  const [form, setForm] = useState({
    accountNumber: "",
    phoneNumber: "",
    amount: ""
  });

  // Validation error messages
  const [errors, setErrors] = useState({});

  // Real token from database after payment
  const [token, setToken] = useState("");

  // Transaction details for summary screen
  const [transactionData, setTransactionData] = useState(null);

  // Status message shown during processing
  const [statusMsg, setStatusMsg] = useState("Initiating payment...");

  // Error message shown on error screen
  const [errMsg, setErrMsg] = useState("");
  const [paidInfo, setPaidInfo] = useState(null);

  // Meter verification status
  // null = not checked, 'checking', 'valid', 'invalid'
  const [meterStatus, setMeterStatus] = useState(null);
  // When a meter belongs to another site, the customer is sent there to pay (into that site's paybill)
  const [correctSite, setCorrectSite] = useState(null);
  const redirectTimer = useRef(null);

  // Link to the meter's own site with the meter number filled in
  const siteLink = (info, meter) => `${info.url}/pay?buy=1&meter=${encodeURIComponent(String(meter || '').trim())}`;
  // Only redirect automatically on the live domain (on localhost we'd jump to production)
  const canAutoRedirect = (url) => {
    try {
      const target = new URL(url);
      const here = window.location.hostname;
      return !/^(localhost|127\.0\.0\.1|\[::1\])$/.test(here) && target.hostname !== here;
    } catch { return false; }
  };
  const sendToSite = (data, meter) => {
    if (!data?.correctUrl) { setCorrectSite(null); return; }
    const info = { url: data.correctUrl, name: data.correctSite, label: data.correctName };
    const link = siteLink(info, meter);
    const auto = canAutoRedirect(link);
    setCorrectSite({ ...info, link, auto });
    clearTimeout(redirectTimer.current);
    if (auto) redirectTimer.current = setTimeout(() => window.location.assign(link), 2500);
  };

  // Arriving from another site with ?meter=…: fill it in and check it straight away
  useEffect(() => {
    const meter = new URLSearchParams(window.location.search).get('meter');
    if (meter) {
      setForm(f => ({ ...f, accountNumber: meter }));
      verifyMeter(meter);
    }
    return () => clearTimeout(redirectTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ref to store polling interval so we can clear it
  const pollRef = useRef(null);

  const isMobile = window.innerWidth < 480;

  // ── FUNCTIONS ──────────────────────────

  // Validates form fields before submission
  const validate = () => {
    const e = {};
    if (!form.accountNumber.trim()) e.accountNumber = "Account number is required";
    if (meterStatus !== 'valid') e.accountNumber = "Please enter a valid meter number";
    if (!form.phoneNumber.trim()) e.phoneNumber = "Phone number is required";
    else if (!/^(07|01|2547|2541)\d{7,8}$/.test(form.phoneNumber.replace(/\s/g, "")))
      e.phoneNumber = "Enter a valid Safaricom number";
    if (!form.amount.trim()) e.amount = "Amount is required";
    else if (isNaN(form.amount) || +form.amount < 1)
      e.amount = "Enter a valid amount (min KES 1)";
    return e;
  };

  // Updates form state and clears error for that field
  const handleChange = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: "" }));
    if (k === 'accountNumber' && correctSite) { clearTimeout(redirectTimer.current); setCorrectSite(null); setMeterStatus(null); }
  };

  // Verifies meter number against external API
  // Called when user leaves the account number field (onBlur)
  const verifyMeter = async (accountNumber) => {
    if (!accountNumber.trim()) return;

    setMeterStatus('checking');
    try {
      const res = await axios.post('/api/payment/verify-meter', {
        accountNumber
      });

      sendToSite(res.data, accountNumber);
      if (res.data.valid) {
        setMeterStatus('valid');
      } else {
        setMeterStatus('invalid');
        setErrors(e => ({
          ...e,
          accountNumber: res.data.message
        }));
      }
    } catch (err) {
      setMeterStatus('invalid');
      setErrors(e => ({
        ...e,
        accountNumber: 'Could not verify meter'
      }));
    }
  };

  // Polls the backend every 5 seconds until the payment resolves.
  // The backend asks Safaricom directly if its callback is late, so a paid
  // transaction is never reported as failed.
  const pollForToken = (transactionId) => {
    let attempts = 0;
    const maxAttempts = 36; // 3 minutes

    pollRef.current = setInterval(async () => {
      attempts++;
      let data = null;
      try {
        data = (await axios.get(`/api/payment/status/${transactionId}`)).data;
      } catch (err) {
        console.error('Polling error:', err.message);
      }

      if (data?.stage === 'done') {
        clearInterval(pollRef.current);
        setToken(data.token);
        setTransactionData(data);
        setStep(STEPS.SUCCESS);
        return;
      }
      if (data?.stage === 'failed') {
        clearInterval(pollRef.current);
        setErrMsg(data.failure_reason ? `M-Pesa: ${data.failure_reason}` : 'Payment was not completed — please try again');
        setStep(STEPS.ERROR);
        return;
      }
      if (data?.stage === 'paid_token_failed') {
        clearInterval(pollRef.current);
        setPaidInfo({ ...data, delayed: false });
        setStep(STEPS.PAID);
        return;
      }

      setStatusMsg(data?.stage === 'paid_processing'
        ? `Payment received${data.mpesa_ref ? ` (${data.mpesa_ref})` : ''} — generating your token…`
        : 'Waiting for you to confirm on your phone…');

      if (attempts >= maxAttempts) {
        clearInterval(pollRef.current);
        // Money may still be on its way — never tell the customer it failed, or they may pay twice
        setPaidInfo({ ...(data || {}), delayed: true });
        setStep(STEPS.PAID);
      }
    }, 5000);
  };

  // Handles Pay button click
  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }

    setStep(STEPS.PROCESSING);
    setStatusMsg("Initiating STK Push...");

    try {
      // Call backend to trigger M-Pesa STK Push
      const res = await axios.post('/api/payment/initiate', {
        accountNumber: form.accountNumber,
        phoneNumber: form.phoneNumber,
        amount: Number(form.amount)
      });

      if (res.data.success) {
        setStatusMsg("Check your phone for M-Pesa prompt...");
        // Start polling for token
        pollForToken(res.data.transactionId);
      }

    } catch (err) {
      setErrMsg(err.response?.data?.message || 'Payment initiation failed');
      if (err.response?.data?.correctUrl) sendToSite(err.response.data, form.accountNumber);
      setStep(STEPS.ERROR);
    }
  };

  // Resets everything back to the form
  const reset = () => {
    setStep(STEPS.FORM);
    setForm({ accountNumber: "", phoneNumber: "", amount: "" });
    setErrors({});
    setToken("");
    setTransactionData(null);
    setErrMsg("");
    setPaidInfo(null);
    setMeterStatus(null);
  };

  // Cleanup — stop polling when component unmounts
  useEffect(() => () => clearInterval(pollRef.current), []);

  // ── RETURN (UI) ────────────────────────
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: isMobile ? "16px 12px" : "24px 20px", boxSizing: "border-box" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes checkPop { 0%{transform:scale(0)} 70%{transform:scale(1.2)} 100%{transform:scale(1)} }
        input::placeholder { color: rgba(148,163,184,0.4); }
        input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; }
        * { -webkit-tap-highlight-color: transparent; }
      `}</style>

      <div style={{ width: "100%", maxWidth: 440, zIndex: 10 }}>

        {/* Brand Header */}
        <div style={{ textAlign: "center", marginBottom: isMobile ? 18 : 24 }}>
          <div style={{ marginBottom: 10 }}>
            <Logo height={isMobile ? 40 : 48} />
          </div>
          <div style={{ color: "#64748b", fontSize: isMobile ? 12 : 13 }}>Smart Water Meter Management & Payments</div>
        </div>

        {/* Card */}
        <div style={{ background: "rgba(15,23,42,0.92)", backdropFilter: "blur(20px)", borderRadius: isMobile ? 16 : 20, border: "1px solid rgba(56,189,248,0.15)", boxShadow: "0 20px 50px rgba(0,0,0,0.5)", padding: isMobile ? "22px 18px" : "30px 28px", animation: "fadeIn 0.5s ease" }}>

          {/* ── FORM STEP ── */}
          {step === STEPS.FORM && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
                <div>
                  <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: isMobile ? 16 : 18 }}>Pay for Water Token</div>
                  <div style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>Enter details to purchase units</div>
                </div>
                <div style={{ background: "rgba(0,168,56,0.12)", border: "1px solid rgba(0,168,56,0.3)", borderRadius: 10, padding: "5px 10px", display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#00a838", animation: "pulse 2s infinite" }} />
                  <span style={{ color: "#00a838", fontSize: isMobile ? 11 : 12, fontWeight: 700 }}>M-PESA</span>
                </div>
              </div>

              {/* Account Number — with meter verification */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Account Number</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    value={form.accountNumber}
                    onChange={e => {
                      handleChange("accountNumber", e.target.value);
                      setMeterStatus(null);
                    }}
                    onBlur={e => verifyMeter(e.target.value)}
                    placeholder="e.g. 0292000016983"
                    style={{
                      width: "100%", boxSizing: "border-box",
                      background: "rgba(255,255,255,0.05)",
                      border: `1.5px solid ${errors.accountNumber ? "#f87171" : meterStatus === 'valid' ? "#10b981" : "rgba(56,189,248,0.25)"}`,
                      borderRadius: 10, padding: "13px 42px 13px 14px",
                      color: "#e2e8f0", fontSize: isMobile ? 16 : 15,
                      outline: "none", fontFamily: "inherit"
                    }}
                    onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                  />
                  {/* Verification status icon */}
                  <span style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>
                    {meterStatus === 'checking' && '…'}
                    {meterStatus === 'valid' && <span style={{ color: '#10b981', fontWeight: 700 }}>✓</span>}
                    {meterStatus === 'invalid' && <span style={{ color: '#f87171', fontWeight: 700 }}>✕</span>}
                  </span>
                </div>
                {errors.accountNumber && !correctSite && (
                  <div style={{ color: "#f87171", fontSize: 12, marginTop: 4 }}>{errors.accountNumber}</div>
                )}
                {correctSite && (
                  <div role="status" style={{ marginTop: 10, background: "rgba(74,155,224,0.15)", border: "1px solid rgba(74,155,224,0.5)", borderRadius: 10, padding: "12px 14px" }}>
                    <div style={{ color: "#ffffff", fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                      This meter is paid for on {correctSite.label}’s site.
                      {correctSite.auto ? ` Taking you to ${correctSite.name}…` : ''}
                    </div>
                    <a href={correctSite.link} style={{ display: "inline-block", background: "#1a5fa4", color: "#ffffff", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
                      {correctSite.auto ? 'Go now →' : `Go to ${correctSite.name} →`}
                    </a>
                  </div>
                )}
              </div>

              {/* Phone Number */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>M-Pesa Phone Number</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="tel"
                    value={form.phoneNumber}
                    onChange={e => handleChange("phoneNumber", e.target.value)}
                    placeholder="e.g. 0712 345 678"
                    style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: `1.5px solid ${errors.phoneNumber ? "#f87171" : "rgba(56,189,248,0.25)"}`, borderRadius: 10, padding: "13px 14px", color: "#e2e8f0", fontSize: isMobile ? 16 : 15, outline: "none", fontFamily: "inherit" }}
                    onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                    onBlur={e => e.target.style.border = `1.5px solid ${errors.phoneNumber ? "#f87171" : "rgba(56,189,248,0.25)"}`}
                  />
                </div>
                {errors.phoneNumber && <div style={{ color: "#f87171", fontSize: 12, marginTop: 4 }}>{errors.phoneNumber}</div>}
              </div>

              {/* Amount */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", color: "#94a3b8", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 }}>Amount (KES)</label>
                <div style={{ position: "relative" }}>
                  <span style={{ position: "absolute", left: 13, top: "50%", transform: "translateY(-50%)", color: "#38bdf8", fontWeight: 700, fontSize: 13 }}>KES</span>
                  <input
                    type="number" inputMode="numeric" min="1"
                    value={form.amount}
                    onChange={e => handleChange("amount", e.target.value)}
                    placeholder="Enter amount"
                    style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: `1.5px solid ${errors.amount ? "#f87171" : "rgba(56,189,248,0.25)"}`, borderRadius: 10, padding: "13px 13px 13px 52px", color: "#e2e8f0", fontSize: isMobile ? 16 : 15, outline: "none", fontFamily: "inherit" }}
                    onFocus={e => e.target.style.border = "1.5px solid #38bdf8"}
                    onBlur={e => e.target.style.border = `1.5px solid ${errors.amount ? "#f87171" : "rgba(56,189,248,0.25)"}`}
                  />
                </div>
                {errors.amount && <div style={{ color: "#f87171", fontSize: 12, marginTop: 4 }}>{errors.amount}</div>}
              </div>

              {/* Quick Select Amounts */}
              <div style={{ marginBottom: 22 }}>
                <div style={{ color: "#64748b", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Quick Select</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                  {[100, 200, 500, 1000].map(v => (
                    <button key={v} onClick={() => handleChange("amount", String(v))}
                      style={{ background: form.amount === String(v) ? "rgba(56,189,248,0.2)" : "rgba(255,255,255,0.04)", border: `1px solid ${form.amount === String(v) ? "#38bdf8" : "rgba(56,189,248,0.15)"}`, borderRadius: 8, padding: "10px 0", color: form.amount === String(v) ? "#38bdf8" : "#94a3b8", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pay Button */}
              <button onClick={handleSubmit} style={{ width: "100%", background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 12, padding: "15px", color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", boxShadow: "0 4px 20px rgba(8,145,178,0.35)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <img src="/mpesa.png" alt = "Mpesa Icon" style={{ height: isMobile ? 18 : 22,objectFit: "contain" }} /> Pay with M-Pesa
              </button>
              <div style={{ textAlign: "center", marginTop: 12, color: "#475569", fontSize: 12 }}>Secured by Digital Solutions</div>
            </>
          )}

          {/* ── PROCESSING STEP ── */}
          {step === STEPS.PROCESSING && (
            <div style={{ textAlign: "center", padding: "16px 0", animation: "fadeIn 0.4s ease" }}>
              <div style={{ width: 64, height: 64, borderRadius: "50%", border: "3px solid rgba(56,189,248,0.15)", borderTop: "3px solid #38bdf8", margin: "0 auto 20px", animation: "spin 1s linear infinite" }} />
              <div style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Processing Payment</div>
              <div style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>{statusMsg}</div>
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 10, padding: 14, textAlign: "left", fontSize: 13 }}>
                {[["Phone", form.phoneNumber], ["Account", form.accountNumber], ["Amount", `KES ${Number(form.amount).toLocaleString()}`]].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0" }}>
                    <span style={{ color: "#64748b" }}>{k}</span>
                    <span style={{ color: k.includes("Amount") ? "#38bdf8" : "#94a3b8", fontWeight: k.includes("Amount") ? 700 : 400 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ color: "#475569", fontSize: 12, marginTop: 14 }}>Check your phone for the M-Pesa STK push</div>
            </div>
          )}

          {/* ── SUCCESS STEP ── */}
          {step === STEPS.SUCCESS && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.5s ease" }}>
              <div style={{ width: 68, height: 68, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "2px solid #10b981", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 30, animation: "checkPop 0.5s ease", color: "#10b981", fontWeight: 700 }}>✓</div>
              <div style={{ color: "#10b981", fontWeight: 800, fontSize: 20, marginBottom: 4 }}>Payment Successful!</div>
              <div style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>Your water token has been generated</div>
              <TokenDisplay token={token} units={transactionData?.units} />
              <div style={{ background: "rgba(255,255,255,0.03)", borderRadius: 12, padding: "14px 16px", marginTop: 16, textAlign: "left" }}>
                <div style={{ color: "#64748b", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 10, fontWeight: 600 }}>Transaction Summary</div>
                {[
                  ["Account", form.accountNumber],
                  ["Phone", form.phoneNumber],
                  ["Amount", `KES ${Number(form.amount).toLocaleString()}`],
                  ...(formatUnits(transactionData?.units) ? [["Units", formatUnits(transactionData.units)]] : []),
                  ["Status", "Confirmed"],
                  ["Date", new Date().toLocaleString()]
                ].map(([k, v]) => (
                  <div key={k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 13, gap: 8 }}>
                    <span style={{ color: "#64748b", flexShrink: 0 }}>{k}</span>
                    <span style={{ color: k === "Amount" ? "#38bdf8" : k === "Status" ? "#10b981" : "#e2e8f0", fontWeight: k === "Amount" ? 700 : 400, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
              <button onClick={reset} style={{ width: "100%", marginTop: 18, background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 12, padding: "13px", color: "#38bdf8", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                + New Payment
              </button>
            </div>
          )}

          {/* ── ERROR STEP ── */}
          {step === STEPS.PAID && paidInfo && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.5s ease" }}>
              <div style={{ width: 68, height: 68, borderRadius: "50%", background: "rgba(251,191,36,0.12)", border: "2px solid #fbbf24", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 30, color: "#fbbf24", fontWeight: 700 }}>…</div>
              <div style={{ color: "#fbbf24", fontWeight: 800, fontSize: 20, marginBottom: 8 }}>
                {paidInfo.delayed && paidInfo.stage !== 'paid_processing' ? 'Still waiting for M-Pesa' : 'Payment received'}
              </div>
              <div style={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>
                {paidInfo.delayed && paidInfo.stage !== 'paid_processing'
                  ? "We haven't had confirmation from M-Pesa yet. If money left your account, your token will be sent to you by SMS and WhatsApp as soon as it's confirmed. Please don't pay again."
                  : "Your payment went through, but your token is taking longer than usual. It will be sent to you by SMS and WhatsApp shortly — you don't need to pay again."}
              </div>
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "10px 14px", color: "#cbd5e1", fontSize: 13, marginBottom: 20, textAlign: "left" }}>
                {paidInfo.mpesa_ref && <div>M-Pesa ref: <b>{paidInfo.mpesa_ref}</b></div>}
                {paidInfo.reference && <div>Our reference: <b>{paidInfo.reference}</b></div>}
                <div style={{ color: "#64748b", marginTop: 4 }}>Quote these if you contact support. You can also use "Retrieve token" on the home page later.</div>
              </div>
              <button onClick={reset} style={{ width: "100%", background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 12, padding: "13px", color: "#38bdf8", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                Done
              </button>
            </div>
          )}

          {step === STEPS.ERROR && (
            <div style={{ textAlign: "center", animation: "fadeIn 0.5s ease" }}>
              <div style={{ width: 68, height: 68, borderRadius: "50%", background: "rgba(248,113,113,0.15)", border: "2px solid #f87171", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", fontSize: 30, color: "#f87171", fontWeight: 700 }}>✕</div>
              <div style={{ color: "#f87171", fontWeight: 800, fontSize: 20, marginBottom: 4 }}>Payment Failed</div>
              <div style={{ color: "#cbd5e1", fontSize: 14, marginBottom: correctSite ? 12 : 20 }}>{errMsg}</div>
              {correctSite && (
                <a href={correctSite.link} style={{ display: "inline-block", marginBottom: 20, background: "#1a5fa4", color: "#ffffff", borderRadius: 8, padding: "10px 16px", fontSize: 14, fontWeight: 700, textDecoration: "none" }}>
                  Go to {correctSite.name} →
                </a>
              )}
              <button onClick={reset} style={{ width: "100%", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 12, padding: "13px", color: "#f87171", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                Try Again
              </button>
            </div>
          )}

        </div>

        <div style={{ textAlign: "center", marginTop: 14, color: "#334155", fontSize: 12, paddingBottom: 8 }}>
          © 2026 Majic Water Systems · Providing modern solutions for modeern problems
        </div>
      </div>
    </div>
  );
}